import { useState, useEffect, useCallback } from 'react';
import { api, getApiUrl } from '../utils/apiConfig';
import { getUserData } from '../utils/userUtils';

/**
 * Custom hook to fetch and manage account settings for a specific account
 * This provides access to account-specific configuration settings
 *
 * @param {string} accountId - The account ID to fetch settings for
 * @param {Object} user - User object (for validation and API calls)
 * @returns {Object} { settings, isLoading, error, refetch, getSetting }
 */
/** An account's settings as { setting_key: setting_value }, from the same request the hook makes. */
export async function loadAccountSettings(accountId, userId) {
  const response = await api.get(getApiUrl(`/api/account-admin/accounts/${accountId}/settings`), { params: { userId } });
  return Object.fromEntries((Array.isArray(response.data) ? response.data : []).map(setting => [setting.setting_key, setting.setting_value]));
}

export const useAccountSettings = (accountId, user) => {
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchKey, setLastFetchKey] = useState(null);

  const fetchAccountSettings = useCallback(async () => {
    // Extract userId from user object (handles both {userId} and {id} formats)
    const effectiveUserId = user?.userId || user?.id;
    
    // Create a unique key for this fetch request
    const fetchKey = `${accountId}-${effectiveUserId}`;

    // Don't fetch if no accountId or user
    if (!accountId || !effectiveUserId) {
      console.log('useAccountSettings: No accountId or user, skipping fetch');
      setSettings({});
      setLastFetchKey(null);
      return;
    }

    // Don't fetch if we already fetched for this combination
    if (lastFetchKey === fetchKey) {
      console.log('useAccountSettings: Already fetched for this account/user combination');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(`useAccountSettings: Fetching settings for account ${accountId}`);

      // Get userId from user data to include in the request
      const userData = getUserData();
      const userId = userData?.userId || effectiveUserId;

      if (!userId) {
        throw new Error('No valid userId found');
      }

      const response = await api.get(getApiUrl(`/api/account-admin/accounts/${accountId}/settings`), {
        params: { userId }
      });

      console.log(`useAccountSettings: Received settings for account ${accountId}:`, response.data);

      // Convert array of settings to key-value object for easier access
      const settingsObject = {};
      if (Array.isArray(response.data)) {
        response.data.forEach(setting => {
          settingsObject[setting.setting_key] = setting.setting_value;
        });
      }

      setSettings(settingsObject);
      setLastFetchKey(fetchKey);
    } catch (err) {
      console.error('useAccountSettings: Error fetching account settings:', err);
      setError(err);

      // Set empty settings on error
      setSettings({});
      setLastFetchKey(fetchKey); // Still mark as fetched to prevent retries
    } finally {
      setIsLoading(false);
    }
  }, [accountId, user?.id, user?.userId, lastFetchKey]);

  // Fetch settings when accountId or user changes
  useEffect(() => {
    fetchAccountSettings();
  }, [fetchAccountSettings]);

  // Provide a manual refetch function
  const refetch = useCallback(() => {
    setLastFetchKey(null); // Reset to force refetch
    fetchAccountSettings();
  }, [fetchAccountSettings]);

  // Helper function to get a specific setting with optional default value
  const getSetting = useCallback((key, defaultValue = null) => {
    return settings[key] !== undefined ? settings[key] : defaultValue;
  }, [settings]);

  // Helper function to get multi-channels setting as an array
  const getMultiChannels = useCallback(() => {
    const multiChannelsValue = getSetting('multi-channels');
    if (!multiChannelsValue) return [];

    const channels = multiChannelsValue
      .split(',')
      .map(channel => channel.trim())
      .filter(channel => channel.length > 0);

    console.log('useAccountSettings: Multi-channels setting parsed:', {
      raw: multiChannelsValue,
      parsed: channels
    });

    return channels;
  }, [getSetting]);

  // Helper function to check if multi-channel mode is enabled
  const isMultiChannelEnabled = useCallback(() => {
    const multiChannels = getMultiChannels();
    return multiChannels.length > 0;
  }, [getMultiChannels]);

  return {
    // Consumers can fail closed while settings still belong to a previous account.
    loadedAccountId: lastFetchKey === `${accountId}-${user?.userId || user?.id}` ? accountId : null,
    settings,
    isLoading,
    error,
    refetch,
    getSetting,
    getMultiChannels,
    isMultiChannelEnabled
  };
};
